import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { InternalInvoiceResponseV3 } from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { invoiceAPI } from 'redux/invoiceAPI';
import { createFilterAdapter } from '@payer-admin-ui/components-v2/src/state/filterAdapter';
import { InvoiceType } from '@payer-admin-ui/api/src/invoices/models/InvoiceType';
import { InvoiceStatus } from '@payer-admin-ui/api/src/invoices/models/InvoiceStatus';
import { defaultSerializeDate, oneYearBack } from '@payer-admin-ui/components-v2/src/utils/dates';
import { MerchantAdminState } from '../../../../redux/store';
import { emptyInvoiceFilters, InvoiceFilters } from '../invoicesGlobalState';

export const searchInvoices = createAsyncThunk<InternalInvoiceResponseV3[], void, { state: MerchantAdminState }>(
  'invoices/create/findCustomer/searchInvoices',
  async (_, { getState }) => {
    const state: any = getState();
    const { appliedFilters, query } = state.invoices.copyInvoiceCustomer.search;
    const trimmed = (query || '').trim();
    const filters: InvoiceFilters = {
      ...appliedFilters,
      search: trimmed,
    };
    const response = await invoiceAPI.fetchPage({ page: 0, pageSize: 50, filters, sort: [] });
    return response.contents;
  },
);

type CopyInvoiceCustomerState = {
  search: {
    query: string;
    isFetching: boolean;
    error: any;
    results: InternalInvoiceResponseV3[];
    filters: InvoiceFilters;
    appliedFilters: InvoiceFilters;
  };
};

const initialFilters: InvoiceFilters = {
  ...emptyInvoiceFilters,
  invoiceType: [InvoiceType.NORMAL, InvoiceType.PREPAYMENT],
  invoiceStatus: [InvoiceStatus.FINAL],
  invoiceDateStart: defaultSerializeDate(oneYearBack()),
};

const initialState: CopyInvoiceCustomerState = {
  search: {
    query: '',
    filters: initialFilters,
    appliedFilters: initialFilters,
    isFetching: false,
    error: null,
    results: [],
  },
};

const filterAdapter = createFilterAdapter<CopyInvoiceCustomerState, InvoiceFilters>({
  selectState: state => state.search,
  emptyFilters: emptyInvoiceFilters,
});

const copyInvoiceCustomerSlice = createSlice({
  name: 'invoices/create/findCustomer',
  initialState,
  reducers: {
    reset: () => initialState,
    setSearchQuery: (state, { payload }: PayloadAction<string>) => {
      state.search.query = payload;
      state.search.isFetching = true;
    },
    resetError: state => ({ ...state, error: null }),
    setError: (state, { payload: message }: PayloadAction<string>) => ({
      ...state,
      error: { message },
    }),
    setFilter: filterAdapter.setFilter,
    clearAllFilters: filterAdapter.clearAllFilters,
    clearFilter: filterAdapter.clearFilter,
    applyFilters: filterAdapter.applyFilters,
  },
  extraReducers: builder => {
    builder.addCase(searchInvoices.pending, state => {
      state.search.isFetching = true;
      state.search.error = null;
    });
    builder.addCase(searchInvoices.fulfilled, (state, { payload }) => {
      state.search.isFetching = false;
      state.search.results = payload;
    });
    builder.addCase(searchInvoices.rejected, (state, { error }) => {
      state.search.isFetching = false;
      state.search.error = error;
    });
  },
});

export const {
  actions: { setError, resetError, setFilter, clearAllFilters, clearFilter, applyFilters, setSearchQuery, reset },
  reducer: copyInvoiceCustomerReducer,
} = copyInvoiceCustomerSlice;
